import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { DdqSessionQuestion } from '@/types/api.types';
import { EditorHost } from './EditorHost';
import { QnaIndexPanel } from './QnaIndexPanel';
import { ResizableSplit } from './ResizableSplit';

export interface DocumentWorkspaceProps {
  /** The session whose responses are under review. */
  sessionId: string;
  /** The questions to index in the right pane. */
  questions: DdqSessionQuestion[];
  /**
   * The document editor to mount in the left pane. Omitted until M3, when a
   * real Word/Excel editor is supplied; a placeholder renders meanwhile.
   */
  editorSlot?: ReactNode;
  /** Optional anchor accessor for click-to-jump binding to the editor. */
  getAnchor?: (question: DdqSessionQuestion) => string | null;
  /**
   * Viewport-relative top px of each answered question's answer in the editor,
   * keyed by question id. Drives the Q&A panel's margin-aligned rows; absent or
   * empty falls back to the compact list.
   */
  rowPositions?: Map<string, number>;
  className?: string;
}

/**
 * The engine-agnostic document-review workspace shell: a two-pane layout with
 * the editor host on the left and the filterable Q&A index on the right,
 * separated by a draggable divider so the reviewer can rebalance the panes. The
 * two panes share selection state via `useWorkspaceStore`, so clicking a
 * question jumps the editor to its anchor and an editor cursor can drive the
 * index — without either side knowing which engine is mounted.
 */
export function DocumentWorkspace({
  sessionId,
  questions,
  editorSlot,
  getAnchor,
  rowPositions,
  className,
}: DocumentWorkspaceProps) {
  return (
    <ResizableSplit
      className={cn('h-full', className)}
      ariaLabel="Resize document editor and Q&A panel"
      left={<EditorHost editorSlot={editorSlot} />}
      right={
        <QnaIndexPanel
          sessionId={sessionId}
          questions={questions}
          getAnchor={getAnchor}
          rowPositions={rowPositions}
        />
      }
    />
  );
}
